import React from "react";
import "../../components/Unique/Login/login_form.css";
import PageHeader from "../../components/Page/page_header";
import { useEffect, useState, useRef } from "react";
import { useSidebar } from "../../components/ui/sidebar"; // Context for managing sidebar state
import "./home.css";
import { useMediaQuery } from "@mui/material";
import MobileLoginPage from "../../pages/MobileView/LoginPage";
import LoginPage from "../../components/Login/LoginPage";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "../../components/ui/chart";
import LoadingScreenDenda from "../../components/Unique/Animation/loading_screen";
import KpiSection from "../../components/Home/KpiSection";
import { TopGamesBarChart } from "../../components/Home/TopGamesBarChart";
import { Select, SelectTrigger, SelectValue, SelectItem, SelectContent } from "../../components/ui/select";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";

const Home = () => {
  const percentageRef = useRef(null);
  const { brand, pages, games} = useSidebar();
  const isMobile = useMediaQuery(`(max-width:768px)`);
  const { mode } = useSidebar();

  const [isLoading, setIsLoading] = useState(true);

  const [kpiData, setKpiData] = useState({
    totalTime: 0,
    totalPlayers: 0,
    gameplays: 0,
    avgGameplays: 0,
    ads: 0,
    percentages: {
      totalTime: 0,
      totalPlayers: 0,
      gameplays: 0,
      avgGameplays: 0,
      ads: 0,
    },
  });

  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    // Only run if pages and games are loaded and non-empty
    if (!Array.isArray(pages) || pages.length === 0 || !Array.isArray(games) || games.length === 0) {
      setIsLoading(false); // Ensure loading ends if no data
      return;
    }

    const loadData = async () => {
      try {
        setIsLoading(true);

        const allowedPageNames = new Set(pages.map(p => p.page_id));
        let allowedGameIds = games.filter(g => allowedPageNames.has(g.name)).map(g => g.game_id);

        // Fetch KPI stats with permissions
        const statsUrl = `${process.env.REACT_APP_CMC_API}/statistics?method=getHomePageStatsWithPermissions&allowed_game_ids=${allowedGameIds.join(",")}`;
        const response = await fetch(statsUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Transform and validate the data
        const validatedData = {
          totalTime: Number(data.totalTime) || 0,
          totalPlayers: Number(data.totalPlayers) || 0,
          gameplays: Number(data.gameplays) || 0,
          avgTime: Number(data.averageTime) || 0,
          avgGameplays: Number(data.avgGameplays) || 0,
          ads: Number(data.ads) || 0,
          percentages: {
            totalTime: Number(data.totalTimePercentage) || 0,
            totalPlayers: Number(data.totalPlayersPercentage) || 0,
            averageTime: Number(data.averageTimePercentage) || 0,
            gameplays: Number(data.gameplaysPercentage) || 0,
            avgGameplays: Number(data.avgGameplaysPercentage) || 0,
            ads: Number(data.adsPercentage) || 0,
          },
        };

        setKpiData(validatedData);

        // Set the chart data using getHomePageChartWithPermissions
        let chartUrl = `${process.env.REACT_APP_CMC_API}/statistics?method=getHomePageChartWithPermissions&allowed_game_ids=${allowedGameIds.join(",")}`;
        const chartResponse = await fetch(chartUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!chartResponse.ok) {
          throw new Error(`HTTP error! status: ${chartResponse.status}`);
        }

        const chartData = await chartResponse.json();

        // Defensive: handle both array/object response
        let formattedChartData = [];
        if (Array.isArray(chartData)) {
          formattedChartData = chartData.map(row => ({
            date: row.date,
            gameplays: row.gameplays || 0,
          }));
        } else if (chartData.date && Array.isArray(chartData.date)) {
          formattedChartData = chartData.date.map((date, index) => ({
            date,
            gameplays: chartData.gameplays[index] || 0,
          }));
        }
        setChartData(formattedChartData);
      } catch (error) {
        console.error("Failed to load KPI or Chart data:", error);
      } finally {
        setIsLoading(false); // Clear loading state after fetch
      }
    };

    loadData();
  }, [pages, games]);

  const chartConfig = {
    gameplays: {
      label: "Gameplays",
      color: "#2563eb",
    },
  };

  // Getting the authentication and sidebar functions from context
  const { authenticated, toggleMobileSidebar } = useSidebar();

  const [timeRange, setTimeRange] = React.useState("90");
  const filteredData = chartData.filter((item) => {
    const date = new Date(item.date);
    const referenceDate = new Date();
    let daysToSubtract = 90;
    if (timeRange === "30") {
      daysToSubtract = 30;
    } else if (timeRange === "7") {
      daysToSubtract = 7;
    }
    const startDate = new Date(referenceDate);
    startDate.setDate(startDate.getDate() - daysToSubtract);
    return date >= startDate;
  });

  const handleTimeRangeChange = (value) => {
    setTimeRange(value)
  };

  return (
    <div className="home">
      {authenticated ? ( // Check if the user is authenticated
        <>
          <div className={"default_page_style !w-full"}>
            <PageHeader title="Home" toggleSidebar={toggleMobileSidebar} />
            {isLoading ? (
              <LoadingScreenDenda text="Fetching some things..." />
            ) : (
              <>
                <KpiSection kpiData={kpiData} mode={mode} percentageRef={percentageRef} />
                <div className="dashb-container">
                  <div className="dashb">
                    <Card className="dark:bg-[--header-color-dark] ml-2 mr-2 sm:ml-0 sm:mr-0">
                      <CardHeader className="flex items-center gap-2 space-y-0 border-b py-5 sm:flex-row">
                        <div className="grid flex-1 gap-1 text-center sm:text-left">
                          <CardTitle>Gameplay Statistics</CardTitle>
                          <CardDescription>Showing total gameplays for the selected date range</CardDescription>
                        </div>
                        <Select
                            value={String(timeRange)}
                            onValueChange={(value) => handleTimeRangeChange(value)}
                        >
                          <SelectTrigger className="w-32 border border-gray-300 rounded-lg px-2 py-1 text-sm dark:text-white dark:bg-[--header-color-dark]">
                            <SelectValue placeholder="Select Days" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="7">Last 7 Days</SelectItem>
                            <SelectItem value="30">Last 30 Days</SelectItem>
                            <SelectItem value="90">Last 90 Days</SelectItem>
                          </SelectContent>
                        </Select>
                      </CardHeader>
                      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6">
                        <ChartContainer config={chartConfig} className="aspect-auto h-[250px] w-full">
                          <AreaChart data={filteredData}>
                            <defs>
                              <linearGradient id="fillGameplays" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="var(--color-gameplays)" stopOpacity={0.9} />
                                <stop offset="95%" stopColor="var(--color-gameplays)" stopOpacity={0.9} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid vertical={false} />
                            <XAxis
                              dataKey="date"
                              tickLine={false}
                              axisLine={false}
                              tickMargin={8}
                              minTickGap={32}
                              tickFormatter={(value) => {
                                const date = new Date(value);
                                return date.toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                });
                              }}
                            />
                            <YAxis axisLine={false} tickLine={false} tickMargin={8} tickFormatter={(value) => value.toLocaleString()} />
                            <ChartTooltip
                              cursor={false}
                              content={
                                <ChartTooltipContent
                                  labelFormatter={(value) => {
                                    return new Date(value).toLocaleDateString("en-US", {
                                      month: "short",
                                      day: "numeric",
                                    });
                                  }}
                                  indicator="dot"
                                />
                              }
                            />
                            <Area dataKey="gameplays" type="natural" fill="url(#fillGameplays)" stroke="var(--color-secondary)" />
                          </AreaChart>
                        </ChartContainer>
                      </CardContent>
                    </Card>
                  </div>
                </div>
                {brand === "Denda" && (
                  <div className="pt-4 sm:ml-14 sm:mr-9 ml-2 mr-2">
                    <TopGamesBarChart />
                  </div>
                )}
              </>
            )}
          </div>
        </>
      ) : // If not authenticated, show login form
      !authenticated && isLoading ? (
        <LoadingScreenDenda text="Loading..." />
      ) : isMobile ? (
        <MobileLoginPage />
      ) : (
        <LoginPage />
      )}
    </div>
  );
};

export default Home;
